// src/pages/Admin/Adherents/useAdherentsAdmin.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { getAllAdherentsWithGroupAndUsers } from "../../../../services/adherentServices";
import { getSettings } from "../../../../services/settingsServices";
import { getGroupsList } from "../../../../services/groupServices";
import { getDojos } from "../../../../services/dojoServices";

import {
  CHUNK,
  extractArray,
  getGroupsText,
  getUsersText,
  neighborsFromSeason,
  normalizeText,
} from "./helpers";

export default function useAdherentsAdmin() {
  const [adherents, setAdherents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");

  const [groupsAll, setGroupsAll] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dojos, setDojos] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [seasonFilter, setSeasonFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [communeFilter, setCommuneFilter] = useState("");
  const [selectedDojo, setSelectedDojo] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [genreFilter, setGenreFilter] = useState("");

  const [sortField, setSortField] = useState("name"); // name | createdAt
  const [sortOrder, setSortOrder] = useState("asc"); // asc | desc

  const [editingStatusId, setEditingStatusId] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [visibleCount, setVisibleCount] = useState(CHUNK);

  const scrollRef = useRef(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(normalizeText(searchTerm));
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const checkForDuplicates = useCallback((rows) => {
    const seen = new Set();
    const duplicates = new Set();

    rows.forEach((adherent) => {
      const key = normalizeText(
        `${adherent?.nom || ""} ${adherent?.prenom || ""}`
      );

      if (!key) return;

      if (seen.has(key)) {
        duplicates.add(key);
      }

      seen.add(key);
    });

    setDuplicateWarning(
      duplicates.size
        ? `Attention, doublon détecté : ${Array.from(duplicates).join(", ")}`
        : null
    );
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      const response = await getAllAdherentsWithGroupAndUsers();
      const rows = extractArray(response);

      setAdherents(rows);
      checkForDuplicates(rows);
    } catch (error) {
      console.error("Erreur rechargement adhérents :", error);
      toast.error("Erreur lors du chargement des adhérents.");
    }
  }, [checkForDuplicates]);

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);

      const [adherentsResponse, settingsResponse, groupsResponse, dojosResponse] =
        await Promise.all([
          getAllAdherentsWithGroupAndUsers(),
          getSettings(),
          getGroupsList(),
          getDojos(),
        ]);

      const adherentsRows = extractArray(adherentsResponse);
      const settingsRows = extractArray(settingsResponse);
      const groupRows = extractArray(groupsResponse);
      const dojoRows = extractArray(dojosResponse);

      setAdherents(adherentsRows);
      checkForDuplicates(adherentsRows);

      setGroupsAll(groupRows);
      setGroups(groupRows);
      setDojos(dojoRows);

      const active = settingsRows?.[0]?.saison_active || "";
      setActiveSeason(active);
      setSeasons(neighborsFromSeason(active));
      setSeasonFilter(active || "");

      setSortField("name");
      setSortOrder("asc");
    } catch (error) {
      console.error("Erreur chargement adhérents :", error);
      toast.error("Erreur lors du chargement des adhérents.");
    } finally {
      setLoading(false);
    }
  }, [checkForDuplicates]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const communes = useMemo(() => {
    return Array.from(
      new Set(adherents.map((adherent) => adherent?.commune).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [adherents]);

  const filtered = useMemo(() => {
    const search = debouncedSearch;

    const rows = adherents.filter((adherent) => {
      const haystack = normalizeText(
        [
          adherent?.nom,
          adherent?.prenom,
          adherent?.email,
          adherent?.email_secondaire,
          adherent?.tel,
          adherent?.tel_secondaire,
          adherent?.licence_number,
          adherent?.commune,
          getGroupsText(adherent),
          getUsersText(adherent),
        ]
          .filter(Boolean)
          .join(" ")
      );

      const matchesSearch = !search || haystack.includes(search);

      const matchesSeason =
        !seasonFilter ||
        String(adherent?.saison_active || "") === String(seasonFilter);

      const matchesStatus =
        !statusFilter ||
        String(adherent?.statut_inscription || "") === String(statusFilter);

      const matchesCommune =
        !communeFilter ||
        String(adherent?.commune || "") === String(communeFilter);

      const matchesGenre =
        !genreFilter || String(adherent?.genre || "") === String(genreFilter);

      const matchesGroup =
        !groupFilter ||
        (Array.isArray(adherent?.Groups) &&
          adherent.Groups.some(
            (group) => String(group?.nom || "") === String(groupFilter)
          ));

      const matchesDojo =
        !selectedDojo ||
        (Array.isArray(adherent?.Groups) &&
          adherent.Groups.some(
            (group) => String(group?.dojo_id || "") === String(selectedDojo)
          ));

      return (
        matchesSearch &&
        matchesSeason &&
        matchesStatus &&
        matchesCommune &&
        matchesGenre &&
        matchesGroup &&
        matchesDojo
      );
    });

    rows.sort((a, b) => {
      if (sortField === "createdAt") {
        const da = new Date(a?.createdAt || a?.created_at || 0).getTime();
        const db = new Date(b?.createdAt || b?.created_at || 0).getTime();

        return sortOrder === "asc" ? da - db : db - da;
      }

      const nameA = `${a?.nom || ""} ${a?.prenom || ""}`.trim();
      const nameB = `${b?.nom || ""} ${b?.prenom || ""}`.trim();

      return sortOrder === "asc"
        ? nameA.localeCompare(nameB, "fr", { sensitivity: "base" })
        : nameB.localeCompare(nameA, "fr", { sensitivity: "base" });
    });

    return rows;
  }, [
    adherents,
    debouncedSearch,
    seasonFilter,
    statusFilter,
    communeFilter,
    genreFilter,
    groupFilter,
    selectedDojo,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    setVisibleCount(CHUNK);

    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [
    debouncedSearch,
    seasonFilter,
    statusFilter,
    communeFilter,
    selectedDojo,
    groupFilter,
    genreFilter,
    sortField,
    sortOrder,
  ]);

  const visibleRows = filtered.slice(0, visibleCount);

  const onScroll = (event) => {
    const element = event.currentTarget;

    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 220) {
      setVisibleCount((current) =>
        current >= filtered.length
          ? current
          : Math.min(current + CHUNK, filtered.length)
      );
    }
  };

  const handleDojoChange = (event) => {
    const dojoId = event.target.value;

    setSelectedDojo(dojoId);
    setGroupFilter("");

    if (!dojoId) {
      setGroups(groupsAll);
      return;
    }

    setGroups(
      groupsAll.filter(
        (group) => String(group?.dojo_id || "") === String(dojoId)
      )
    );
  };

  const toggleNameSort = () => {
    setSortField("name");

    setSortOrder((current) =>
      sortField === "name" && current === "asc" ? "desc" : "asc"
    );
  };

  const toggleCreatedSort = () => {
    setSortField("createdAt");

    setSortOrder((current) =>
      sortField === "createdAt" && current === "asc" ? "desc" : "asc"
    );
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSeasonFilter(activeSeason || "");
    setStatusFilter("");
    setCommuneFilter("");
    setSelectedDojo("");
    setGroupFilter("");
    setGroups(groupsAll);
    setGenreFilter("");

    setSortField("name");
    setSortOrder("asc");
  };

  return {
    state: {
      adherents,
      loading,

      seasons,
      activeSeason,

      groups,
      groupsAll,
      dojos,

      searchTerm,
      seasonFilter,
      statusFilter,
      communeFilter,
      selectedDojo,
      groupFilter,
      genreFilter,

      communes,
      duplicateWarning,

      sortField,
      sortOrder,

      editingStatusId,
      visibleCount,
      scrollRef,

      filtered,
      visibleRows,
    },

    setters: {
      setSearchTerm,
      setSeasonFilter,
      setStatusFilter,
      setCommuneFilter,
      setGroupFilter,
      setGenreFilter,
      setEditingStatusId,
    },

    handlers: {
      refreshAll,
      fetchInitialData,
      handleDojoChange,
      toggleNameSort,
      toggleCreatedSort,
      resetFilters,
      onScroll,
    },
  };
}